import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Paperclip, Send, X, Loader2 } from 'lucide-react';
import { useGql } from '@ah/design';
import { Header } from '@ah/design/mobile';
import { ModerationMenu } from '@ah/moderation/mobile';
import type {
  ChatMessage,
  ChatUser,
  Conversation,
  MessageAttachment,
} from '../shared/types.ts';
import {
  CONVERSATIONS_QUERY,
  MARK_READ,
  MESSAGES_QUERY,
  MESSAGE_ADDED_SUB,
  SEND_MESSAGE,
  SET_TYPING,
  TYPING_SUB,
  UPLOAD_ATTACHMENT,
} from '../shared/queries.ts';
import {
  MessageBubble,
  MessagesSkeleton,
  convTitle,
  displayName,
  formatDayDivider,
  sameDay,
} from './_parts.tsx';

const ME_QUERY = /* GraphQL */ `query Me { me { id } }`;

type PendingAttachment = {
  originalName: string;
  url: string;
  fileSize: number;
  mimeType: string;
};

export function ChatThread() {
  const { conversationId = '' } = useParams();
  const navigate = useNavigate();
  const gql = useGql();

  const { data: me } = useQuery({
    queryKey: ['me', 'id'],
    queryFn: () => gql.request<{ me: { id: string } }>(ME_QUERY),
  });
  const currentUserId = me?.me.id ?? '';

  const { data: convsData } = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => gql.request<{ conversations: Conversation[] }>(CONVERSATIONS_QUERY),
  });
  const conversation = useMemo(
    () => convsData?.conversations.find((c) => c.id === conversationId) ?? null,
    [convsData, conversationId],
  );

  const { data: msgsData, isLoading: loadingMessages } = useQuery({
    queryKey: ['chat', 'messages', conversationId],
    queryFn: () =>
      gql.request<{ messages: ChatMessage[] }>(MESSAGES_QUERY, {
        conversationId,
        limit: 50,
      }),
    enabled: !!conversationId,
  });

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  useEffect(() => {
    setMessages(msgsData?.messages ?? []);
  }, [msgsData]);

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Map<string, { user: ChatUser; at: number }>>(
    new Map(),
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const didInitialScrollRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = (smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  const markRead = () => {
    gql.request(MARK_READ, { conversationId }).catch(() => {});
    window.dispatchEvent(
      new CustomEvent('chat:read', { detail: { conversationId } }),
    );
  };

  // reset the initial-scroll flag whenever the conversation changes
  useEffect(() => {
    didInitialScrollRef.current = false;
  }, [conversationId]);

  // initial load: pin to bottom (after layout + once more after images settle)
  useLayoutEffect(() => {
    if (!msgsData || didInitialScrollRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    didInitialScrollRef.current = true;
    el.scrollTop = el.scrollHeight;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    const t = setTimeout(() => {
      el.scrollTop = el.scrollHeight;
    }, 120);
    markRead();
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgsData]);

  // subscribe to new messages for this conversation
  useEffect(() => {
    if (!conversationId) return;
    const unsub = gql.subscribe<{ onMessageAdded: ChatMessage }>(
      MESSAGE_ADDED_SUB,
      { conversationId },
      (data) => {
        const msg = data.onMessageAdded;
        setMessages((prev) => {
          if (prev.find((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        const fromOther = msg.author.id !== currentUserId;
        if (fromOther && isNearBottomRef.current) {
          markRead();
        }
        if (!fromOther || isNearBottomRef.current) {
          requestAnimationFrame(() => scrollToBottom(true));
        }
      },
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, gql, currentUserId]);

  // typing subscription
  useEffect(() => {
    if (!conversationId) return;
    const unsub = gql.subscribe<{
      onTypingStatus: { conversationId: string; isTyping: boolean; user: ChatUser };
    }>(TYPING_SUB, { conversationId }, (data) => {
      const { user, isTyping } = data.onTypingStatus;
      if (user.id === currentUserId) return;
      setTypingUsers((prev) => {
        const next = new Map(prev);
        if (isTyping) next.set(user.id, { user, at: Date.now() });
        else next.delete(user.id);
        return next;
      });
    });
    return () => unsub();
  }, [conversationId, gql, currentUserId]);

  // expire typing indicators after 4s of silence
  useEffect(() => {
    if (typingUsers.size === 0) return;
    const t = setInterval(() => {
      setTypingUsers((prev) => {
        const now = Date.now();
        let changed = false;
        const next = new Map(prev);
        for (const [id, entry] of next) {
          if (now - entry.at > 4000) {
            next.delete(id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [typingUsers]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - (el.scrollTop + el.clientHeight) < 120;
  };

  const sendTyping = (isTyping: boolean) => {
    gql.request(SET_TYPING, { conversationId, isTyping }).catch(() => {});
  };

  const handleTextChange = (value: string) => {
    setText(value);
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    }
    if (!value) return;
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    } else {
      sendTyping(true);
    }
    typingTimeoutRef.current = setTimeout(() => {
      sendTyping(false);
      typingTimeoutRef.current = null;
    }, 3000);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const base64 = await new Promise<string>((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res((reader.result as string).split(',')[1]);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
        const data = await gql.request<{ uploadMessageAttachment: PendingAttachment }>(
          UPLOAD_ATTACHMENT,
          {
            fileName: file.name,
            mimeType: file.type,
            data: base64,
            fileSize: file.size,
          },
        );
        setPending((prev) => [...prev, data.uploadMessageAttachment]);
      }
    } catch {
      // ignore
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSend = async () => {
    const content = text.trim();
    if (!content && pending.length === 0) return;
    if (!conversation) return;

    const attachments = pending;
    const tempId = `temp-${Date.now()}`;
    const me = conversation.participants.find((p) => p.user.id === currentUserId)?.user ?? null;
    if (me) {
      const optimistic: ChatMessage = {
        id: tempId,
        conversationId,
        content,
        author: me,
        attachments: attachments.map((a, i) => ({ id: `tmp-${i}`, ...a }) as MessageAttachment),
        linkUrl: null,
        linkTitle: null,
        linkDescription: null,
        linkImageUrl: null,
        linkDomain: null,
        readBy: [],
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimistic]);
    }

    setText('');
    setPending([]);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    sendTyping(false);
    setSending(true);
    requestAnimationFrame(() => scrollToBottom(true));

    try {
      const data = await gql.request<{ sendChatMessage: ChatMessage }>(SEND_MESSAGE, {
        conversationId,
        content,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      setMessages((prev) => {
        const withoutDup = prev.filter(
          (m) => m.id !== data.sendChatMessage.id && m.id !== tempId,
        );
        return [...withoutDup, data.sendChatMessage];
      });
    } catch {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setText(content);
      setPending(attachments);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !('ontouchstart' in window)) {
      e.preventDefault();
      handleSend();
    }
  };

  const title = conversation ? convTitle(conversation, currentUserId) : 'Chat';

  // Compute read-receipt position for DMs
  const readIndicatorForId: string | null = useMemo(() => {
    if (!conversation || conversation.isGroup) return null;
    const other = conversation.participants.find((p) => p.user.id !== currentUserId);
    if (!other?.lastReadAt) return null;
    const lastReadAt = new Date(other.lastReadAt).getTime();
    let lastMineId: string | null = null;
    for (const m of messages) {
      if (m.author.id !== currentUserId) continue;
      if (new Date(m.createdAt).getTime() <= lastReadAt) lastMineId = m.id;
    }
    return lastMineId;
  }, [conversation, messages, currentUserId]);

  const typingLabel = (() => {
    if (typingUsers.size === 0) return null;
    const names = Array.from(typingUsers.values()).map((t) => displayName(t.user));
    if (names.length === 1) return `${names[0]} is typing…`;
    return `${names.join(', ')} are typing…`;
  })();

  // For 1:1 threads, expose Block + Report; group threads can only be
  // reported as a whole. `otherParticipant` resolves to null for groups
  // since there's no single "subject user" to block.
  const otherParticipant =
    conversation && !conversation.isGroup
      ? conversation.participants.find((p) => p.user.id !== currentUserId)?.user
      : null;

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header
        title={title}
        backTo="/chat"
        trailing={
          conversation ? (
            <ModerationMenu
              targetType="chat_conversation"
              targetId={conversation.id}
              subjectUserId={otherParticipant?.id}
              subjectName={otherParticipant ? displayName(otherParticipant) : null}
              reportTitle="Report this chat"
              onBlocked={() => navigate('/chat')}
            />
          ) : null
        }
      />

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-3"
      >
        {loadingMessages ? (
          <MessagesSkeleton />
        ) : messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
            Say hello 👋
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {messages.map((m, i) => {
              const prev = i > 0 ? messages[i - 1] : null;
              const showDay = !prev || !sameDay(prev.createdAt, m.createdAt);
              const showAuthor =
                !!conversation?.isGroup &&
                m.author.id !== currentUserId &&
                (!prev || prev.author.id !== m.author.id);
              const mine = m.author.id === currentUserId;
              return (
                <div key={m.id} className="flex flex-col gap-1.5">
                  {showDay && (
                    <div className="flex justify-center my-2">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">
                        {formatDayDivider(m.createdAt)}
                      </span>
                    </div>
                  )}
                  <MessageBubble
                    msg={m}
                    mine={mine}
                    showAuthor={showAuthor}
                    isGroup={!!conversation?.isGroup}
                  />
                  {readIndicatorForId === m.id && (
                    <div className="flex justify-end pr-1 -mt-1">
                      <span className="text-xs text-muted-foreground">Read</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {typingLabel && (
        <div className="px-4 py-1 text-xs text-muted-foreground">{typingLabel}</div>
      )}

      {pending.length > 0 && (
        <div className="px-3 py-2 flex flex-wrap gap-2 border-t border-border">
          {pending.map((att, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs"
            >
              {att.mimeType.startsWith('image/') ? (
                <img src={att.url} alt="" className="w-5 h-5 object-cover rounded" />
              ) : (
                <Paperclip className="w-3 h-3" />
              )}
              <span className="truncate max-w-[140px]">{att.originalName}</span>
              <button
                type="button"
                onClick={() => setPending((prev) => prev.filter((_, j) => j !== i))}
                aria-label="Remove attachment"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <footer
        className="border-t border-border p-2 flex items-end gap-2"
        style={{ paddingBottom: `calc(env(safe-area-inset-bottom) + 0.5rem)` }}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          multiple
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
          onChange={handleFileChange}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          aria-label="Attach file"
          className="flex items-center justify-center w-9 h-9 rounded-lg text-foreground hover:bg-accent shrink-0"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Paperclip className="w-5 h-5" />
          )}
        </button>

        <textarea
          ref={textareaRef}
          rows={1}
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message…"
          className="flex-1 resize-none rounded-2xl border border-border bg-background px-3 py-2 text-[15px] leading-snug outline-none focus:ring-2 focus:ring-primary/30 max-h-[120px]"
        />

        <button
          type="button"
          onClick={handleSend}
          disabled={sending || (!text.trim() && pending.length === 0)}
          aria-label="Send"
          className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground disabled:opacity-40 shrink-0"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </footer>
    </div>
  );
}
